/**
 * SlideDeckView — THE way a slide deck is previewed, everywhere.
 *
 * A deck used to look like a different object depending on where it was opened:
 * proper 16:9 slide sheets in the artifact panel, differently-styled cards in
 * the Library viewer, and a flat wall of text in the meta-agent's preview of a
 * Library file. Three renderers, three looks, one file.
 *
 * So there is one component now, and the three surfaces call it:
 *   - the artifact panel (regular agents and the meta-agent),
 *   - the Library file viewer,
 *   - the Library-file preview inside the meta-agent's side panel.
 *
 * Two shapes of input reach it, so it takes a normalised `Slide[]` and each
 * caller adapts:
 *   - an ARTIFACT carries a doc_model already shaped as title/subtitle/bullets;
 *   - a STORED .pptx is read out of the file as shapes and paragraphs.
 * `slidesFromDeckEnvelope` does the second, so the mapping lives here beside
 * the renderer rather than being reinvented per surface.
 */

import React from 'react';
import './SlideDeckView.css';

export interface SlideBullet {
  text: string;
  level?: number;
  /** false → render as a plain line, with no marker invented for it. */
  bulleted?: boolean;
  /** This line is the slide's heading, IN PLACE. Used by the stored-deck path,
   *  where the heading is rarely the first thing on the slide — a brand strip
   *  often sits above it — and lifting it to the top would show the slide in an
   *  order the file does not have, and in an order the editor does not use. */
  heading?: boolean;
}

export interface Slide {
  id?: string | number;
  title?: string;
  subtitle?: string;
  bullets?: Array<SlideBullet | string>;
  /** 'title' | 'section' | 'content' — drives vertical centring. */
  layout?: string;
}

const asBullet = (b: SlideBullet | string): SlideBullet =>
  (typeof b === 'object' ? b : { text: b });

export const SlideDeckView: React.FC<{ slides: Slide[] }> = ({ slides }) => (
  <div className="artifact-slides">
    {slides.map((s, i) => {
      const layout = (s.layout || 'content').toLowerCase();
      const items = (s.bullets || []).map(asBullet).filter((b) => (b.text || '').trim());
      // A deck line only takes a marker when the deck itself gives it one, so
      // the list is split rather than forced into a single <ul>.
      return (
        <div className={`artifact-slide artifact-slide--${layout}`} key={s.id ?? i}>
          <div className="artifact-slide__num">{i + 1}</div>
          <div className="artifact-slide__inner">
            {s.title && <h3 className="artifact-slide__title" dir="auto">{s.title}</h3>}
            {s.subtitle && <p className="artifact-slide__subtitle" dir="auto">{s.subtitle}</p>}
            {items.length > 0 && (
              <div className="artifact-slide__body">
                {groupRuns(items).map((group, gi) =>
                  group.bulleted ? (
                    <ul className="artifact-slide__bullets" key={gi}>
                      {group.items.map((b, bi) => (
                        <li key={bi} className={`lvl-${Math.min(b.level || 0, 4)}`} dir="auto">
                          {b.text}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <React.Fragment key={gi}>
                      {group.items.map((b, bi) => (b.heading ? (
                        <h3 className="artifact-slide__title" key={bi}>{b.text}</h3>
                      ) : (
                        <p className="artifact-slide__line" key={bi}>{b.text}</p>
                      )))}
                    </React.Fragment>
                  ),
                )}
              </div>
            )}
          </div>
        </div>
      );
    })}
  </div>
);

/** Consecutive lines that agree about being bulleted travel together, so a real
 *  list stays one <ul> instead of becoming a stack of one-item lists. */
function groupRuns(items: SlideBullet[]): Array<{ bulleted: boolean; items: SlideBullet[] }> {
  const out: Array<{ bulleted: boolean; items: SlideBullet[] }> = [];
  for (const item of items) {
    // Artifact bullets carry no `bulleted` flag — they are, by definition, bullets.
    const bulleted = item.bulleted !== false && !item.heading;
    const last = out[out.length - 1];
    if (last && last.bulleted === bulleted) last.items.push(item);
    else out.push({ bulleted, items: [item] });
  }
  return out;
}

/** WHICH PARAGRAPH IS THE SLIDE'S TITLE? One rule, wherever the deck is drawn.
 *
 *  A real PowerPoint title placeholder answers it outright. Failing that — and
 *  the decks we generate are plain text boxes, so usually there is none — the
 *  biggest type on the slide is its heading, which is exactly how a reader sees
 *  it. Taking "the first line" instead promoted the brand strip above the title
 *  and demoted the actual title to body text.
 *
 *  Returned as shape/paragraph coordinates rather than a flat index so the
 *  EDITOR can style the same line as the preview does. Both surfaces asking one
 *  function is the point: a title that moved in one and not the other would make
 *  editing a deck look like editing a different deck.
 */
export function deckTitlePos(slide: any): { sh: number; p: number } | null {
  const shapes = Array.isArray(slide?.shapes) ? slide.shapes : [];
  const isTitleShape = (sh: any) =>
    String(sh?.kind || '').toLowerCase().includes('title')
    || /title/i.test(String(sh?.name || ''));

  const cands: Array<{ sh: number; p: number; size: number; fromTitleShape: boolean }> = [];
  shapes.forEach((shape: any, sh: number) => {
    const paras = Array.isArray(shape?.paras) ? shape.paras : [];
    paras.forEach((para: any, p: number) => {
      const runs = Array.isArray(para?.runs) ? para.runs : [];
      if (!runs.map((r: any) => r?.t || '').join('').trim()) return;
      cands.push({
        sh, p,
        size: Math.max(0, ...runs.map((r: any) => Number(r?.sz) || 0)),
        fromTitleShape: isTitleShape(shape),
      });
    });
  });
  if (!cands.length) return null;

  const placed = cands.find((c) => c.fromTitleShape);
  if (placed) return { sh: placed.sh, p: placed.p };

  const biggest = Math.max(...cands.map((c) => c.size));
  const rest = cands.filter((c) => c.size < biggest).map((c) => c.size);
  const nextBiggest = rest.length ? Math.max(...rest) : 0;
  // Only when it genuinely stands out; a slide of evenly-sized lines has no
  // title and is better drawn as plain body than given a false heading.
  if (biggest > 0 && biggest >= nextBiggest * 1.25) {
    const win = cands.find((c) => c.size === biggest)!;
    return { sh: win.sh, p: win.p };
  }
  return null;
}

/** Shapes-and-paragraphs (a stored .pptx, read by the vault) -> the slide model.
 *
 *  The slide's title (see `deckTitlePos`) becomes the title and everything else
 *  its body — which is how the artifact model is shaped, so both inputs land on
 *  the same rendering. `bu` (the paragraph's real bullet state, carried from the
 *  file) decides which lines get a marker.
 */
export function slidesFromDeckEnvelope(deck: any): Slide[] {
  const slides = Array.isArray(deck?.slides) ? deck.slides : [];
  return slides.map((slide: any, i: number) => {
    const pos = deckTitlePos(slide);
    const shapes = Array.isArray(slide?.shapes) ? slide.shapes : [];

    // Document order, heading marked in place rather than lifted to the top.
    // The heading is often NOT the first thing on a slide — a brand strip
    // usually sits above it — so hoisting it drew the slide in an order the
    // file does not have, and in an order the editor does not use either.
    const lines: SlideBullet[] = [];
    let headings = 0;
    shapes.forEach((shape: any, sh: number) => {
      const paras = Array.isArray(shape?.paras) ? shape.paras : [];
      paras.forEach((para: any, p: number) => {
        const runs = Array.isArray(para?.runs) ? para.runs : [];
        const text = runs.map((r: any) => r?.t || '').join('').trim();
        if (!text) return;
        const heading = !!pos && pos.sh === sh && pos.p === p;
        if (heading) headings += 1;
        lines.push({ text, level: para?.lvl || 0, bulleted: para?.bu === true, heading });
      });
    });

    const layout = String(slide?.layout || '').toLowerCase();
    return {
      id: slide?.n ?? i,
      bullets: lines,
      // A sparse slide reads as a title/section card — vertically centred, the
      // same treatment the artifact renderer gives one.
      layout: /title|section/.test(layout) || lines.length - headings <= 3
        ? 'title' : 'content',
    };
  });
}
